/** Typed Desk operations. Services own scoring, deadlines and counter arithmetic. */
import { api } from "@/api/client";
import { ScorioSocket } from "@/api/ws";
import { getIdToken } from "@/firebase";
import { useFeedback } from "@/stores/feedback";
import type { Member, HeatRider } from "@/types";
export type ProtestKind = "protest" | "technical_incident" | "correction" | "review_request";
export type FilerRole = "rider" | "team_rep" | "any";
export type SignRole = "chief_judge" | "chief_scorer";
export interface ProtestRules {
  allowed: boolean;
  kinds: ProtestKind[];
  filers: FilerRole[];
  fee_cents?: number | null;
  currency: string;
  window_minutes: number;
  window_from: "posting" | "official" | "occurrence";
  free_per_season?: number;
  free_counter_scope?: "wwa_season" | "series" | "competition";
  refund_if_upheld?: boolean;
  correction_window_minutes?: number | null;
  correction_window_from?: "posting" | "official";
}
export interface Posting {
  id: string;
  division_id: string;
  round_id: null;
  heat_id: string | null;
  kind: "provisional" | "official" | "corrected";
  posting_no: number;
  version: number;
  posted_at: string;
  panel_approved_at: string | null;
  signed_chief_judge_at: string | null;
  signed_chief_scorer_at: string | null;
  required_signatures: number;
  cut_line: number | null;
  supersedes_id: string | null;
  protest_deadline_at: string | null;
  official_at: string | null;
  official: boolean;
  correction_opened_at: string | null;
  pdf_ready: boolean;
  snapshot?: {
    event_name: string;
    panel_required: boolean;
    pack: string;
    protest_config: ProtestRules;
    heats: {
      heat_id: string;
      heat_name: string;
      results: {
        competitors: {
          heat_rider_id: string;
          name: string;
          final: number | string | null;
          status_label: string | null;
          placement: number | null;
          attempts: { id: string; attempt_number: number; under_protest?: boolean }[];
        }[];
      };
    }[];
  };
}
export interface Protest {
  id: string;
  posting_id: string | null;
  heat_id: string | null;
  heat_rider_id: string | null;
  attempt_number: number | null;
  kind: ProtestKind;
  filed_by_name: string;
  filed_at: string;
  deadline_at: string | null;
  fee_cents: number | null;
  currency: string | null;
  fee_paid: boolean;
  free_protest_used: boolean;
  grounds: string;
  status: "open" | "upheld" | "dismissed" | "withdrawn" | "invalid";
  decision: string | null;
  decided_at: string | null;
  attachments: string[];
}
export interface EligibleScope {
  division_id: string;
  name: string;
  eligible: boolean;
  heats: { id: string; name: string; eligible: boolean }[];
}
export interface DeskState {
  postings: Posting[];
  protests: Protest[];
  eligible_scopes: EligibleScope[];
  rounds: never[];
  open_protests_count: number;
  unsigned_postings_count: number;
}
export interface DeskCompetition {
  timezone: string;
  id: string;
  name: string;
  owner_id: string;
  features: { protests?: boolean };
  protest: ProtestRules | null;
  sanction?: { pack?: string } | null;
}
export interface DeskContext {
  competition: DeskCompetition;
  roles: string[];
}
export interface PostingCommand {
  division_id: string;
  heat_id?: string;
  kind: Posting["kind"];
  posted_at?: string;
  cut_line?: number;
  required_signatures?: number;
  supersedes_id?: string;
}
export interface ProtestCommand {
  kind: ProtestKind;
  posting_id?: string;
  heat_id: string;
  heat_rider_id?: string;
  attempt_number?: number;
  filer_heat_rider_id?: string;
  filed_by_name: string;
  filed_by_role: FilerRole;
  grounds: string;
  fee_paid: boolean;
  attachments: string[];
  free_protest_used?: boolean;
  override?: boolean;
  override_reason?: string;
}
export interface DecisionCommand {
  status: Exclude<Protest["status"], "open">;
  decision: string;
  outcome?: "rescore";
}
export interface DeskClient {
  context(userId: string | null): Promise<DeskContext>;
  state(): Promise<DeskState>;
  riders(heatId: string): Promise<HeatRider[]>;
  attempts(heatId: string): Promise<{ heat_rider_id: string; attempt_number: number }[]>;
  post(body: PostingCommand, key: string): Promise<Posting>;
  approve(id: string, version: number, key: string): Promise<Posting>;
  sign(id: string, role: SignRole, version: number, key: string): Promise<Posting>;
  correct(
    id: string,
    body: { reason: string; override: boolean; expected_version: number },
    key: string,
  ): Promise<Posting>;
  file(body: ProtestCommand, key: string): Promise<Protest>;
  decide(id: string, body: DecisionCommand): Promise<Protest>;
  pdf(id: string): Promise<Blob>;
  csv(): Promise<Blob>;
  subscribe?(refresh: () => void): () => void;
}
export function createDeskApi(
  competitionId: string,
  http: Pick<typeof api, "get" | "post" | "put" | "download"> = api,
  getToken: () => Promise<string | null> = getIdToken,
): DeskClient {
  const base = `/api/competitions/${encodeURIComponent(competitionId)}`;
  // Desk never queues or silently retries a write. A deliberate resubmission keeps
  // its original key; the UI reuses it while the body remains unchanged.
  const write = async <T>(
    method: "POST" | "PUT",
    path: string,
    body: unknown,
    key?: string,
  ): Promise<T> => {
    try {
      const options = { ...(key ? { idempotencyKey: key } : {}), canRetry: () => false };
      return await (method === "POST"
        ? http.post<T>(path, body, options)
        : http.put<T>(path, body, options));
    } catch (error) {
      // The retained dialog owns deliberate resubmission. Preserve the failure
      // notice, including after navigation, without an unrelated stale retry.
      const feedback = useFeedback.getState();
      feedback.toasts
        .filter((toast) => toast.key === `api:${method}:${path}`)
        .forEach((toast) =>
          feedback.show({
            key: toast.key,
            kind: toast.kind,
            message: toast.message,
            href: toast.href,
          }),
        );
      throw error;
    }
  };
  return {
    context: async (userId) => {
      const [competition, members] = await Promise.all([
        http.get<DeskCompetition>(base),
        http.get<Member[]>(`${base}/members`),
      ]);
      return {
        competition,
        roles:
          competition.owner_id === userId
            ? ["admin"]
            : (members.find((m) => m.user_id === userId)?.roles ?? []),
      };
    },
    state: async () => {
      const [state, protests] = await Promise.all([
        http.get<DeskState>(`${base}/desk-state`),
        http.get<Protest[]>(`${base}/protests`),
      ]);
      return { ...state, protests };
    },
    riders: (id) => http.get<HeatRider[]>(`${base}/heats/${encodeURIComponent(id)}/riders`),
    attempts: async (id) =>
      (
        await http.get<{ attempts: { heat_rider_id: string; attempt_number: number }[] }>(
          `${base}/heats/${encodeURIComponent(id)}/panel-status`,
        )
      ).attempts,
    post: (body, key) => write<Posting>("POST", `${base}/postings`, body, key),
    approve: (id, version, key) =>
      write<Posting>(
        "POST",
        `${base}/postings/${encodeURIComponent(id)}/panel-approve`,
        { expected_version: version },
        key,
      ),
    sign: (id, role, version, key) =>
      write<Posting>(
        "POST",
        `${base}/postings/${encodeURIComponent(id)}/sign`,
        { role, expected_version: version },
        key,
      ),
    correct: (id, body, key) =>
      write<Posting>("POST", `${base}/postings/${encodeURIComponent(id)}/correct`, body, key),
    file: (body, key) => write<Protest>("POST", `${base}/protests`, body, key),
    decide: (id, body) => write<Protest>("PUT", `${base}/protests/${encodeURIComponent(id)}`, body),
    pdf: (id) => http.download(`${base}/postings/${encodeURIComponent(id)}.pdf`),
    csv: () => http.download(`${base}/export/protests.csv`),
    subscribe: (refresh) => {
      const origin =
        import.meta.env.VITE_WS_URL ??
        `${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}`;
      const socket = new ScorioSocket(`${origin}${base}/ws`, { getToken });
      let pending: ReturnType<typeof setTimeout> | undefined;
      const off = socket.subscribe((event) => {
        const kind = String(event.type);
        if (["posting_update", "protest_update"].includes(kind)) {
          clearTimeout(pending);
          pending = undefined;
          refresh();
        } else if (
          [
            "scores_update",
            "results_update",
            "heats_update",
            "heat_status",
            "release_state",
          ].includes(kind) &&
          !pending
        ) {
          // One read covers a scoring/release burst, including decodeWire's
          // results_update -> scores_update compatibility mapping.
          pending = setTimeout(() => {
            pending = undefined;
            refresh();
          }, 500);
        }
      });
      const status = socket.onStatus((connected) => {
        if (connected) refresh();
      });
      socket.connect();
      return () => {
        clearTimeout(pending);
        off();
        status();
        socket.disconnect();
      };
    },
  };
}
export function saveDeskDownload(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
